import { CodeStatus, PermitType, SignatureType } from "../../common/enums";
import { formElement } from "../forms/FormElement";
import HotWorkOnShipPermitFormState from "../forms/Permits/HotWorkOnShipPermitFormState";
import Person, { Person_default, Person_toFormElement } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_empty } from "../Signature";
import { permitContentVersions } from "../WorkPermit";

type HotWorkOnShipPermit = {
    id: string;
    entityId: number;
    sequenceIdentifier: string;
    remindMe: boolean;
    remindMeDate: Date;
    startDate: Date;
    endDate: Date;
    adminComment: string;
    status: CodeStatus;
    description: string;
    positions: Position[];
    anonymousUserEmail: string;

    permitProviderSignature: Signature;
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    version: number;
    hasAttachedFiles: boolean;
    renewalOfPermitSequenceIdentifier: string;
    renewedByPermitSequenceIdentifier: string;
    createdByUserName: string;
    rejectionComment: string;
    permitName: PermitType.HotWorkOnShipPermit;

    shipName: string;
    callSign: string;
    terminalAcceptsWork: boolean;
    ehCoordinatorAcceptsWork: boolean;
    commanderOrRepresentative: Person;

    isAnonymousPermit: boolean;
    initialStartDate: Date;
    extendedEndDate: Date;

    departmentId: number;

    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canManageAttachments: boolean;
    canManagePositions: boolean;
    canReject: boolean;
    canManageAdminComment: boolean;
    canRenew: boolean;
    canManagePositionPolygons: boolean;
};

export default HotWorkOnShipPermit;

export function HotWorkOnShipPermit_normalize(permit: HotWorkOnShipPermit): HotWorkOnShipPermit {
    const normalizedPermit: HotWorkOnShipPermit = {
        adminComment: permit.adminComment ?? "",
        anonymousUserEmail: permit.anonymousUserEmail ?? "",
        callSign: permit.callSign ?? "",
        canAccept: permit.canAccept ?? false,
        canManageAdminComment: permit.canManageAdminComment ?? false,
        canManageAttachments: permit.canManageAttachments ?? false,
        canManagePositions: permit.canManagePositions ?? false,
        canReject: permit.canReject ?? false,
        canRenew: permit.canRenew ?? false,
        canUpdate: permit.canUpdate ?? false,
        commanderOrRepresentative: permit.commanderOrRepresentative ?? Person_default(),
        createdByUserName: permit.createdByUserName ?? "",
        departmentId: permit.departmentId ?? 0,
        description: permit.description ?? "",
        ehCoordinatorAcceptsWork: permit.ehCoordinatorAcceptsWork ?? false,
        endDate: permit.endDate ? new Date(permit.endDate) : new Date(),
        entityId: permit.entityId ?? 0,
        expirationReminderEmail: permit.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: permit.expirationReminderPhoneNumber ?? "",
        hasAttachedFiles: permit.hasAttachedFiles ?? false,
        id: permit.id ?? "",
        extendedEndDate: permit.extendedEndDate ? new Date(permit.extendedEndDate) : new Date(),
        initialStartDate: permit.initialStartDate ? new Date(permit.initialStartDate) : new Date(),
        isAnonymousPermit: permit.isAnonymousPermit ?? false,
        permitName: permit.permitName ?? PermitType.HotWorkOnShipPermit,
        permitProviderSignature: permit.permitProviderSignature ?? Signature_empty(SignatureType.PermitProvider),
        positions: permit.positions ?? [],
        rejectionComment: permit.rejectionComment ?? "",
        remindMe: permit.remindMe ?? false,
        remindMeDate: permit.remindMeDate ? new Date(permit.remindMeDate) : new Date(),
        shipName: permit.shipName ?? "",
        status: permit.status ?? CodeStatus.Registered,
        renewalOfPermitSequenceIdentifier: permit.renewalOfPermitSequenceIdentifier ?? "",
        renewedByPermitSequenceIdentifier: permit.renewedByPermitSequenceIdentifier ?? "",
        sequenceIdentifier: permit.sequenceIdentifier ?? "",
        startDate: permit.startDate ? new Date(permit.startDate) : new Date(),
        terminalAcceptsWork: permit.terminalAcceptsWork ?? false,
        version: permit.version ?? 1,
        canManagePositionPolygons: permit.canManagePositionPolygons ?? false,
        contentVersion: permit.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };

    return normalizedPermit;
}

export function HotWorkOnShipPermit_toFormState(permit: HotWorkOnShipPermit): HotWorkOnShipPermitFormState {
    const formState: HotWorkOnShipPermitFormState = {
        adminComment: formElement(permit.adminComment),
        anonymousUserEmail: formElement(permit.anonymousUserEmail),
        callSign: formElement(permit.callSign),
        commanderOrRepresentative: Person_toFormElement(permit.commanderOrRepresentative),
        description: formElement(permit.description),
        endDate: formElement(permit.endDate),
        expirationReminderEmail: formElement(permit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(permit.expirationReminderPhoneNumber),
        positions: permit.positions.map(Position_toPositionPickerRow),
        ehCoordinatorAcceptsWork: formElement(permit.ehCoordinatorAcceptsWork),
        remindMe: formElement(permit.remindMe),
        remindMeDate: formElement(permit.remindMeDate),
        shipName: formElement(permit.shipName),
        startDate: formElement(permit.startDate),
        terminalAcceptsWork: formElement(permit.terminalAcceptsWork)
    };

    return formState;
}
